import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService, Product, Case, Article, Banner, Material } from '../services/api';

interface UseApiOptions {
  immediate?: boolean;
  dependencies?: any[];
}

// 用于数组数据的 Hook
interface UseApiArrayResult<T> {
  data: T[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// 用于单个数据的 Hook
interface UseApiItemResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// 数组数据的通用 Hook
export function useApiArray<T>(
  apiCall: () => Promise<T[]>,
  options: UseApiOptions = {}
): UseApiArrayResult<T> {
  const { immediate = true, dependencies = [] } = options;
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiCall();
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, ...dependencies]);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediate, ...dependencies]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}

// 单个数据的通用 Hook
export function useApiItem<T>(
  apiCall: () => Promise<T | null>,
  options: UseApiOptions = {}
): UseApiItemResult<T> {
  const { immediate = true, dependencies = [] } = options;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(immediate);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiCall();
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiCall, ...dependencies]);

  useEffect(() => {
    if (immediate) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [immediate, ...dependencies]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
  };
}

// 产品相关 hooks
export function useProducts(params?: {
  sort?: string;
  limit?: number;
}): UseApiArrayResult<Product> {
  const paramsStr = useMemo(() => JSON.stringify(params), [params]);
  const apiCall = useCallback(() => apiService.getProducts(params), [params]);
  return useApiArray(apiCall, { dependencies: [paramsStr] });
}

export function useProduct(id: string): UseApiItemResult<Product> {
  const apiCall = useCallback(() => apiService.getProduct(id), [id]);
  return useApiItem(apiCall, { dependencies: [id] });
}

// 案例相关 hooks
export function useCaseStudies(params?: {
  sort?: string;
  limit?: number;
}): UseApiArrayResult<Case> {
  const paramsStr = useMemo(() => JSON.stringify(params), [params]);
  const apiCall = useCallback(() => apiService.getCaseStudies(params), [params]);
  return useApiArray(apiCall, { dependencies: [paramsStr] });
}

export function useCaseStudy(id: string): UseApiItemResult<Case> {
  const apiCall = useCallback(() => apiService.getCaseStudy(id), [id]);
  return useApiItem(apiCall, { dependencies: [id] });
}

// 文章相关 hooks
export function useArticles(params?: {
  sort?: string;
  limit?: number;
}): UseApiArrayResult<Article> {
  const paramsStr = useMemo(() => JSON.stringify(params), [params]);
  const apiCall = useCallback(() => apiService.getArticles(params), [params]);
  return useApiArray(apiCall, { dependencies: [paramsStr] });
}

export function useArticle(id: string): UseApiItemResult<Article> {
  const apiCall = useCallback(() => apiService.getArticle(id), [id]);
  return useApiItem(apiCall, { dependencies: [id] });
}

// 轮播图相关 hooks
export function useBanners(params?: {
  sort?: string;
  limit?: number;
}): UseApiArrayResult<Banner> {
  const paramsStr = useMemo(() => JSON.stringify(params), [params]);
  const apiCall = useCallback(() => apiService.getBanners(params), [params]);
  return useApiArray(apiCall, { dependencies: [paramsStr] });
}

// 器件材料相关 hooks
export function useMaterials(params?: {
  sort?: string;
  limit?: number;
}): UseApiArrayResult<Material> {
  const paramsStr = useMemo(() => JSON.stringify(params), [params]);
  const apiCall = useCallback(() => apiService.getMaterials(params), [params]);
  return useApiArray(apiCall, { dependencies: [paramsStr] });
}

export function useMaterial(id: string): UseApiItemResult<Material> {
  const apiCall = useCallback(() => apiService.getMaterial(id), [id]);
  return useApiItem(apiCall, { dependencies: [id] });
}
